import { Dungeon } from '../dungeon/Dungeon';
import { Vec3 } from '../types';
import { Geometry } from '../renderer/geometry';
import { shuffle, random } from '../utils/random';

export interface Arrow {
  position: Vec3;
  rotation: number;
  collected: boolean;
}

export const ARROW_COLLECT_RADIUS = 0.35;

export function placeArrow(
  dungeon: Dungeon,
  excludeCell: { x: number; z: number },
  excludeRadius: number,
  occupiedCells: Set<string>
): Arrow | null {
  const candidates: { x: number; z: number }[] = [];

  for (let z = 1; z < dungeon.height - 1; z++) {
    for (let x = 1; x < dungeon.width - 1; x++) {
      if (!dungeon.isWalkable(x, z)) continue;
      if (occupiedCells.has(`${x},${z}`)) continue;

      const dx = x - excludeCell.x;
      const dz = z - excludeCell.z;
      if (Math.sqrt(dx * dx + dz * dz) < excludeRadius) continue;

      candidates.push({ x, z });
    }
  }

  if (candidates.length === 0) return null;

  shuffle(candidates);
  const cell = candidates[0];
  const worldPos = dungeon.cellToWorld(cell.x, cell.z);

  return {
    position: { x: worldPos.x, y: 0.5, z: worldPos.z },
    rotation: random() * Math.PI * 2,
    collected: false
  };
}

export function generateFloatingArrowGeometry(): Geometry {
  const vertices: number[] = [];
  const ny = [0, 1, 0]; // Normal facing up (flat on XZ plane)

  // Flat triangle head: tip at -Z, wings at base
  // tip(0, 0, -0.18), left(-0.06, 0, -0.06), right(0.06, 0, -0.06)
  const tip  = [0,     0, -0.18];
  const left = [-0.06, 0, -0.06];
  const right = [0.06, 0, -0.06];

  // Top face (CCW from above)
  vertices.push(...tip,   ...ny, 0, 0, 0);
  vertices.push(...left,  ...ny, 0, 0, 0);
  vertices.push(...right, ...ny, 0, 0, 0);
  // Bottom face (reverse winding so visible from below too)
  vertices.push(...tip,   ...[0, -1, 0], 0, 0, 0);
  vertices.push(...right, ...[0, -1, 0], 0, 0, 0);
  vertices.push(...left,  ...[0, -1, 0], 0, 0, 0);

  // Flat rectangle shaft: from z=-0.06 to z=+0.10
  const hw = 0.03; // half width
  const s0 = -0.06;  // shaft start (meets triangle base)
  const s1 = 0.10;   // shaft end

  // Top face (CCW from above for +Y normal)
  vertices.push(-hw, 0, s0, ...ny, 0, 0, 0);
  vertices.push(-hw, 0, s1, ...ny, 0, 0, 0);
  vertices.push( hw, 0, s1, ...ny, 0, 0, 0);

  vertices.push(-hw, 0, s0, ...ny, 0, 0, 0);
  vertices.push( hw, 0, s1, ...ny, 0, 0, 0);
  vertices.push( hw, 0, s0, ...ny, 0, 0, 0);

  // Bottom face (CCW from below for -Y normal)
  vertices.push(-hw, 0, s0, ...[0, -1, 0], 0, 0, 0);
  vertices.push( hw, 0, s1, ...[0, -1, 0], 0, 0, 0);
  vertices.push(-hw, 0, s1, ...[0, -1, 0], 0, 0, 0);

  vertices.push(-hw, 0, s0, ...[0, -1, 0], 0, 0, 0);
  vertices.push( hw, 0, s0, ...[0, -1, 0], 0, 0, 0);
  vertices.push( hw, 0, s1, ...[0, -1, 0], 0, 0, 0);

  return {
    vertices: new Float32Array(vertices),
    vertexCount: vertices.length / 9
  };
}
